import React, { useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";

/*
 * Contact & Donate — modernized page.
 * Keeps the original EmailJS submission flow (REACT_APP_CONTACT_API)
 * with a mailto: fallback so the form always has a working path.
 * Donation flow uses the existing Square checkout link.
 */

const SQUARE_DONATE_URL = "https://square.link/u/hJzYmg7H";
const CONTACT_EMAIL = "support@zeltechnology.org";

const Contact = () => {
  useDocTitle("ZelT | Contact & Donate");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isRemoteRequest = searchParams.get("request") === "remote";
  const isVolunteerApp = searchParams.get("request") === "volunteer";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [requestType, setRequestType] = useState(
    searchParams.get("request") === "remote"
      ? "remote"
      : searchParams.get("request") === "volunteer"
      ? "volunteer"
      : "general"
  );
  const [sending, setSending] = useState(false);

  const clearInput = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setMessage("");
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent text-gray-700";

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);

    // B4: contact submissions create a trackable ticket when the sender is a
    // logged-in member. Non-members keep the email fallback (no account to
    // attach the ticket to).
    const token = localStorage.getItem("zelt_token");
    if (token) {
      axios
        .post(
          `${process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001"}/tickets`,
          {
            category: requestType === "remote" || requestType === "hardware" || requestType === "digital" ? requestType : "general",
            subject: `[${requestType}] ${message.slice(0, 60)}`,
            body: `Name: ${firstName} ${lastName}\nPhone: ${phone || "n/a"}\n\n${message}`,
            priority: requestType === "remote" ? "high" : "normal",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((response) => {
          clearInput();
          Notiflix.Report.success(
            `Ticket #${response.data.ticket.id} created! 🎫`,
            "A confirmation email is on its way. Track this request in the Member Area → My Requests.",
            "Okay"
          );
          if (requestType === "remote") {
            navigate("/remote");
          }
        })
        .catch((error) => {
          const msg =
            error.response?.data?.message ||
            "Something went wrong — please email us at " + CONTACT_EMAIL;
          Notiflix.Report.failure("Couldn't create the ticket", msg, "Okay");
        })
        .finally(() => setSending(false));
      return;
    }

    const api = process.env.REACT_APP_CONTACT_API;
    // requestType tags the submission so requests route to the right workflow
    const typeLabel =
      requestType === "remote"
        ? "[REMOTE SESSION REQUEST]"
        : requestType === "hardware"
        ? "[HARDWARE ASSISTANCE]"
        : requestType === "digital"
        ? "[DIGITAL GROWTH INQUIRY]"
        : "";

    if (!api) {
      // No form API configured yet — fall back to the visitor's mail client
      const subject = encodeURIComponent(
        `${typeLabel} Support request from ${firstName} ${lastName}`.trim()
      );
      const body = encodeURIComponent(
        `Name: ${firstName} ${lastName}\nEmail: ${email}\nPhone: ${phone}\nRequest type: ${requestType}\n\n${message}`
      );
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
      Notiflix.Notify.info("Opening your email app to send your message.");
      setSending(false);
      return;
    }

    let fData = new FormData();
    fData.append("first_name", firstName);
    fData.append("last_name", lastName);
    fData.append("email", email);
    fData.append("phone_number", phone);
    fData.append("message", `[${requestType}] ${message}`);

    axios({
      method: "post",
      url: api,
      data: fData,
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        clearInput();
        setRequestType("general");
        Notiflix.Report.success(
          "Success",
          requestType === "remote"
            ? "Your remote session request is in! A technician will reach out to schedule a time."
            : response.data.message,
          "Okay"
        );
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message ||
          "Something went wrong. Please email us directly at " + CONTACT_EMAIL;
        Notiflix.Report.failure("An error occurred", msg, "Okay");
      })
      .finally(() => setSending(false));
  };

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full py-12 lg:py-20 bg-white">
        <div className="mx-4 max-w-6xl mx-auto">
          {/* header */}
          <div className="text-center mb-12" data-aos="zoom-in">
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-3">
              {isRemoteRequest
                ? "🖥️ ZelConnect Remote — Request"
                : isVolunteerApp
                ? "🤝 ZelConnect — Volunteer Application"
                : "Contact & Support Our Mission"}
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {isRemoteRequest
                ? "You're requesting a supervised remote session. The form below is pre-set for that — here's exactly what happens next."
                : isVolunteerApp
                ? "Tell us about your technical experience and availability. Applications are reviewed by real people — approved volunteers get assigned cases from our community."
                : "Questions, tech trouble, or the desire to help? Every message is read by a human who cares."}
            </p>
          </div>

          {/* volunteer application guidance card */}
          {isVolunteerApp && (
            <div
              className="mb-12 rounded-2xl overflow-hidden shadow-2xl border-2 border-purple-300 max-w-4xl mx-auto"
              data-aos="fade-up"
            >
              <div
                className="p-5 text-white"
                style={{ background: "linear-gradient(90deg, #9198e5, #1e3a8a)" }}
              >
                <h2 className="text-xl font-extrabold">
                  🤝 About Volunteering with ZelConnect
                </h2>
              </div>
              <div className="bg-white p-6 text-gray-600 text-sm leading-relaxed space-y-3">
                <p>
                  <b className="text-blue-900">What volunteers do:</b> accept
                  assigned help cases from our community, run supervised
                  ZelConnect Remote sessions (with the same safeguards our
                  staff follow), and share plain-language guidance.
                </p>
                <p>
                  <b className="text-blue-900">What we require:</b> a free
                  member account, a short application (below), a review by our
                  team, and agreement to our technician code of conduct — the
                  same member-first safeguards apply to everyone.
                </p>
                <p>
                  <b className="text-blue-900">What you get:</b> verified
                  volunteer status, assigned cases that match your skills, and
                  the satisfaction of being the reason someone's technology
                  works again.
                </p>
              </div>
            </div>
          )}

          {/* remote-session walkthrough card */}
          {isRemoteRequest && (
            <div
              className="mb-12 rounded-2xl overflow-hidden shadow-2xl border-2 border-teal-300 max-w-4xl mx-auto"
              data-aos="fade-up"
            >
              <div
                className="p-5 text-white"
                style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
              >
                <h2 className="text-xl font-extrabold">
                  📋 What Happens Next — Step by Step
                </h2>
                <p className="text-white/90 text-sm mt-1">
                  Keep this card handy during your appointment — it's your
                  checklist.
                </p>
              </div>
              <div className="bg-white divide-y divide-gray-100">
                <div className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center text-sm">1</p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">Send the request form (below)</h3>
                    <p className="text-gray-600 text-sm">It's already set to "Remote support session" — just describe the problem and when you're usually available. A technician emails you to confirm an appointment time.</p>
                  </div>
                </div>
                <div className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center text-sm">2</p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">At your appointment — download the small RustDesk program</h3>
                    <p className="text-gray-600 text-sm">Go to <b>rustdesk.com</b> → Download. Windows: choose the <b>x64.exe</b>. Mac (Apple chip): <b>aarch64.dmg</b>; (Intel): <b>x86_64.dmg</b>. If Windows shows a blue "Windows protected your PC" message, click <b>More info → Run anyway</b> — it's the official RustDesk program.</p>
                  </div>
                </div>
                <div className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center text-sm">3</p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">Point it at ZelTechnology's server (once)</h3>
                    <p className="text-gray-600 text-sm">In RustDesk: click the <b>⋯ three dots</b> next to your ID → <b>Network</b> → unlock. Enter exactly: <b>ID Server:</b> <code className="bg-gray-100 px-1 rounded">54.69.163.169</code> · <b>Relay Server:</b> <code className="bg-gray-100 px-1 rounded">54.69.163.169</code> · <b>Key:</b> <code className="bg-gray-100 px-1 rounded break-all">VDGcMdIXdGUQeCn7BqBM6lonuQDce4T3ShlW+Ev66MU=</code> · leave <b>API Server empty</b>. Click Apply — the bottom status should say <b>Ready</b>.</p>
                  </div>
                </div>
                <div className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center text-sm">4</p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">Read us your ID + one-time password</h3>
                    <p className="text-gray-600 text-sm">Your RustDesk window shows a <b>9-digit ID</b> and a <b>one-time password</b> — read both to the technician when asked. The password changes every launch; that's the security working.</p>
                  </div>
                </div>
                <div className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center text-sm">5</p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">Click Allow — and you're in control the whole time</h3>
                    <p className="text-gray-600 text-sm">When the technician connects, your screen shows a permission prompt — click <b>Allow</b>. (Macs also ask for "Screen Recording" permission — click Configure and toggle RustDesk on.) A red <b>End Session</b> button is on your screen the entire time; one click ends everything.</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-teal-50 text-center">
                <p className="text-sm text-gray-700">
                  Questions before your appointment? 📞 Call{" "}
                  <b>858-215-1048</b> — and remember: we only connect when{" "}
                  <b>you</b> requested it. Ever see anything else? It's a
                  scam — hang up.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* LEFT: contact info + donate */}
            <div className="space-y-6" data-aos="fade-up">
              {/* donate card */}
              <div
                className="rounded-2xl p-8 text-white shadow-2xl"
                style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #36395a 100%)" }}
              >
                <h2 className="text-2xl font-extrabold mb-2">
                  💙 Support the Mission
                </h2>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  ZelTechnology is a nonprofit. Donations keep our services
                  free for everyone who needs them — every dollar funds
                  helpers, tools, and protection for our community.
                </p>
                <a
                  href={SQUARE_DONATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-teal-400 text-blue-900 font-extrabold px-6 py-3 rounded-xl hover:bg-teal-300 transition shadow-lg"
                >
                  <span className="text-2xl">🦈</span> Donate via Square
                </a>
                <p className="text-gray-400 text-xs mt-4">
                  Secure checkout handled by Square. ZelTechnology is a
                  nonprofit — donations support our free services.
                </p>
              </div>

              {/* info card */}
              <div
                className="rounded-2xl p-8 text-white shadow-2xl"
                style={{ background: "linear-gradient(135deg, #12bfbe 0%, #9198e5 100%)" }}
              >
                <h2 className="text-2xl font-extrabold mb-6">Reach Us Directly</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">✉️</span>
                    <div>
                      <p className="font-bold">Email</p>
                      <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        className="text-white/90 hover:underline break-all"
                      >
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📞</span>
                    <div>
                      <p className="font-bold">Phone</p>
                      <p className="text-white/90">Tel: 858-215-1048</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📍</span>
                    <div>
                      <p className="font-bold">Mail</p>
                      <p className="text-white/90">
                        Zeltechnology
                        <br />
                        #1055
                        <br />
                        11160 Rancho Carmel Dr STE
                        <br />
                        106
                        <br />
                        San Diego, CA 92128
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🌐</span>
                    <div>
                      <p className="font-bold">Follow</p>
                      <div className="flex gap-3 mt-1">
                        <a
                          href="https://www.facebook.com/zeltechnology/"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="ZelTechnology on Facebook"
                          className="rounded-full bg-white h-8 w-8 flex items-center justify-center text-blue-900 hover:scale-110 transition"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="fill-current">
                            <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"></path>
                          </svg>
                        </a>
                        <a
                          href="https://www.linkedin.com/company/zeltechnology-inc-"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="ZelTechnology on LinkedIn"
                          className="rounded-full bg-white h-8 w-8 flex items-center justify-center text-blue-900 hover:scale-110 transition"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="fill-current">
                            <circle cx="4.983" cy="5.009" r="2.188"></circle>
                            <path d="M9.237 8.855v12.139h3.769v-6.003c0-1.584.298-3.118 2.262-3.118 1.937 0 1.961 1.811 1.961 3.218v5.904H21v-6.657c0-3.27-.704-5.783-4.526-5.783-1.835 0-3.065 1.007-3.568 1.96h-.051v-1.66H9.237zm-6.142 0H6.87v12.139H3.095z"></path>
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: contact form */}
            <div data-aos="fade-up">
              <div className="bg-slate-900 rounded-2xl p-8 shadow-2xl h-full">
                <div className="bg-white rounded-xl p-6 md:p-8">
                  <h2 className="text-2xl font-extrabold text-blue-900 mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-gray-500 text-sm mb-6">
                    Tell us what's going on — the more detail, the better we
                    can help.
                  </p>
                  <form onSubmit={sendEmail} className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={inputClass}
                        maxLength={50}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={inputClass}
                        maxLength={50}
                        required
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      maxLength={254}
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={inputClass}
                      maxLength={20}
                    />
                    <select
                      value={requestType}
                      onChange={(e) => setRequestType(e.target.value)}
                      className={inputClass}
                      aria-label="What is this about?"
                    >
                      <option value="general">General question</option>
                      <option value="remote">🖥️ Remote support session</option>
                      <option value="hardware">🎁 Hardware assistance</option>
                      <option value="digital">📈 Digital Growth services</option>
                      <option value="volunteer">🤝 Volunteer application</option>
                    </select>
                    <textarea
                      placeholder={
                        requestType === "remote"
                          ? "Describe the problem and your availability — a technician will confirm a time."
                          : "How can we help?"
                      }
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className={inputClass}
                      rows={6}
                      maxLength={2000}
                      required
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50 inline-flex items-center justify-center w-full px-6 py-3 text-lg font-bold shadow-xl rounded-2xl"
                    >
                      {sending ? "Sending..." : "Send Message ➤"}
                    </button>
                    <p className="text-center text-gray-400 text-xs">
                      We reply to every message — usually within a day.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
